import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Button,
  Link,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Link as LinkIcon,
  ContentCopy as ContentCopyIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Place as PlaceIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import { useDispatch, useSelector } from 'react-redux';
import { getMeetingById, clearCurrentMeeting } from '../../store/slices/meetingsSlice';
import { toast } from 'react-toastify';
import { CALENDAR_SCHEDULE_PATH } from '../../navigations/path';

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return 'N/A';
  try {
    const date = new Date(dateTimeString.replace(' ', 'T') + 'Z');
    return date.toLocaleString();
  } catch {
    return dateTimeString;
  }
};

const getStatusColor = (statusName) => {
  const colorMap = {
    Upcoming: 'primary',
    Completed: 'success',
    Cancelled: 'error',
    Rescheduled: 'warning',
    'In Progress': 'info',
    Meeting: 'primary',
  };
  return colorMap[statusName] || 'default';
};

const MeetingView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentMeeting, loading, error } = useSelector((state) => state.meetings);

  useEffect(() => {
    if (!id) return;
    dispatch(getMeetingById(id));
    return () => dispatch(clearCurrentMeeting());
  }, [id, dispatch]);

  const handleBack = () => navigate(CALENDAR_SCHEDULE_PATH);

  const handleCopyLink = (url) => {
    if (!url) return;
    navigator.clipboard.writeText(url).then(
      () => toast.success('Link copied to clipboard'),
      () => toast.error('Failed to copy link')
    );
  };

  if (loading && !currentMeeting) {
    return (
      <PageLayout>
        <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center', minHeight: 320 }}>
          <CircularProgress sx={{ mt: 4 }} />
        </Container>
      </PageLayout>
    );
  }

  if (error && !currentMeeting) {
    return (
      <PageLayout>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Back to Calendar
          </Button>
        </Container>
      </PageLayout>
    );
  }

  const meeting = currentMeeting;
  if (!meeting) {
    return null;
  }

  const lead = meeting.lead || {};
  const status = meeting.status || {};
  const attendees = meeting.attendees || [];

  return (
    <PageLayout>
      <Container maxWidth="2xl" sx={{ py: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Calendar
        </Button>

        <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
          Meeting Details
        </Typography>

        <Grid container spacing={3}>
          {/* Meeting info */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <ScheduleIcon color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Meeting
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Chip
                    label={status.name || '—'}
                    color={getStatusColor(status.name)}
                    size="small"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Time zone: {meeting.time_zone || '—'}
                  </Typography>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Start
                  </Typography>
                  <Typography variant="body1">
                    {formatDateTime(meeting.start_date_time)}
                  </Typography>
                </Box>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    End
                  </Typography>
                  <Typography variant="body1">
                    {formatDateTime(meeting.end_date_time)}
                  </Typography>
                </Box>
                {meeting.description && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Description
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {meeting.description}
                    </Typography>
                  </Box>
                )}
                {meeting.google_event_url && (
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Link
                      href={meeting.google_event_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <LinkIcon fontSize="small" />
                      Open in Google Calendar
                    </Link>
                    <Tooltip title="Copy link">
                      <IconButton
                        size="small"
                        onClick={() => handleCopyLink(meeting.google_event_url)}
                        sx={{ color: 'primary.main' }}
                        aria-label="Copy Google Calendar link"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                  Created: {meeting.created_at || '—'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Lead */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PersonIcon color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Lead
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  {lead.name || '—'}
                </Typography>
                {lead.dba_name && (
                  <Typography variant="body2" color="text.secondary">
                    DBA: {lead.dba_name}
                  </Typography>
                )}
                {lead.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography variant="body2">{lead.email}</Typography>
                  </Box>
                )}
                {lead.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">{lead.phone}</Typography>
                  </Box>
                )}
                {lead.address && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5 }}>
                    <PlaceIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
                    <Typography variant="body2">{lead.address}</Typography>
                  </Box>
                )}
                {lead.description && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Note
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {lead.description}
                    </Typography>
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                  Created: {lead.created_at || '—'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Attendees */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <GroupIcon color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Attendees ({attendees.length})
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {attendees.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No attendees
                  </Typography>
                ) : (
                  <List disablePadding>
                    {attendees.map((attendee) => (
                      <ListItem
                        key={attendee.id}
                        divider
                        sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="subtitle1" fontWeight={600}>
                              {attendee.name || attendee.username || '—'}
                            </Typography>
                          }
                          secondary={
                            <Box component="span">
                              {attendee.email && (
                                <Typography variant="body2" component="span" display="block">
                                  {attendee.email}
                                </Typography>
                              )}
                              {attendee.phone && (
                                <Typography variant="body2" color="text.secondary" component="span" display="block">
                                  {attendee.phone}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </PageLayout>
  );
};

export default MeetingView;
